import React, { useState } from 'react';
import { Card, Badge, Button, Collapse } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faGlobe, faBriefcase, faTasks, faChevronDown, faChevronUp, faEdit, faTrashAlt, faPaperPlane, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { Task } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns'; // For relative dates
import TaskComments from './TaskComments'; // Import TaskComments

interface TaskCardProps {
  task: Task;
  // Callback props for actions
  onEdit: (task: Task) => void;
  onDelete: (taskId: number) => void;
  onApply: (taskId: number) => void;
  onMessageOwner: (ownerId: number, ownerName: string | null) => void;
  onConnectOwner: (ownerId: number, ownerName: string | null) => void;
  // Add new prop
  startExpanded?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({ 
    task, 
    onEdit, 
    onDelete, 
    onApply, 
    onMessageOwner, 
    onConnectOwner,
    startExpanded = false // Destructure with default value
}) => {
  // Initialize state based on the prop
  const [isExpanded, setIsExpanded] = useState(startExpanded);
  const { user } = useAuth(); // Get current user

  // Format skills as badges
  const skillsHtml = task.skills?.map(skill => 
    <Badge pill bg="light" text="dark" key={skill} className="me-1 mb-1">{skill}</Badge>
  ) || null;

  // Location display
  let locationDisplay: React.ReactNode = null;
  if (task.isRemote) {
    locationDisplay = (
        <span className="me-3" title="Remote">
            <FontAwesomeIcon icon={faGlobe} className="text-info me-1" /> Remote
        </span>
    );
  } else if (task.locationName) {
    locationDisplay = (
        <span className="me-3" title={task.locationName}>
            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-danger me-1" /> {task.locationName}
        </span>
    );
  } else {
     locationDisplay = (
        <span className="me-3 text-muted" title="Location not specified">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" /> N/A
        </span>
     );
  }

  // Status badge class
  let statusClass = 'primary';
  if (task.status === 'ASSIGNED') statusClass = 'success';
  if (task.status === 'CLOSED') statusClass = 'secondary';

  // Format date
  const postedDate = formatDistanceToNow(new Date(task.createdAt), { addSuffix: true });

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const isOwner = user?.id === task.ownerId;

  return (
    <Card className={`shadow-sm task-card ${isExpanded ? 'expanded' : ''}`}>
      <Card.Body className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
                <h5 className="mb-1 card-title fw-bold">{task.title}</h5>
                {task.owner?.name && task.owner?.id && (
                    <p className="text-muted mb-2 small">
                        Posted by <Link to={`/profile/${task.owner.id}`} className="text-decoration-none">{task.owner.name}</Link>
                    </p>
                )}
            </div>
             {/* Placeholder for potential future action button like save/bookmark */}
            {/* <Button variant="link" size="sm" className="p-0 text-secondary">
                <FontAwesomeIcon icon={faBookmark} /> 
            </Button> */}
        </div>

        {/* Description Snippet */}
        <Card.Text className="mb-3 text-muted flex-grow-1" style={{ fontSize: '0.9rem' }}>
            {task.description.substring(0, 120)}{task.description.length > 120 ? '...' : ''}
        </Card.Text>

        {/* Skills */}
        {skillsHtml && skillsHtml.length > 0 && (
            <div className="mb-3 task-skills">
                {skillsHtml}
            </div>
        )}

        {/* Meta Info Footer */}
        <div className="d-flex align-items-center text-muted small mt-auto">
            {locationDisplay}
            <Badge bg={statusClass} className="me-1">{task.status}</Badge>
            <Badge bg={task.type === 'JOB' ? 'info' : 'warning'} text="dark">
                <FontAwesomeIcon icon={task.type === 'JOB' ? faBriefcase : faTasks} className="me-1" />
                {task.type}
            </Badge>
            <span className="ms-auto" title={new Date(task.createdAt).toLocaleString()}>{postedDate}</span>
        </div>
      </Card.Body>

      {/* Card Footer with Toggle Button */} 
      <Card.Footer className="bg-white border-top-0 pt-0">
         <Button 
            variant="outline-primary" 
            size="sm" 
            className="w-100 d-flex justify-content-center align-items-center" 
            onClick={toggleExpand} 
            aria-controls={`task-details-${task.id}`} 
            aria-expanded={isExpanded}
         >
             {isExpanded ? 'Hide Details' : 'View Details'}
             <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} className="ms-2" />
         </Button>
      </Card.Footer>

      {/* Expanded Details Section */} 
      <Collapse in={isExpanded}>
        <div id={`task-details-${task.id}`}>
          <Card.Body className="border-top pt-3">
             <div className="task-full-description mb-3">
                <h6 className="fw-bold">Full Description</h6>
                <p style={{ fontSize: '0.9rem' }} className="text-muted">{task.description}</p>
             </div>

             {/* Task/Job Specific Details */} 
             <div className="task-specific-details mb-3">
                 <h6 className="fw-bold">Details</h6>
                 <ul className="list-unstyled text-muted small">
                     {task.type === 'TASK' && task.deadline && (
                         <li><strong>Deadline:</strong> {new Date(task.deadline).toLocaleString()}</li>
                     )}
                     {task.type === 'TASK' && task.estimatedHours && (
                         <li><strong>Est. Hours:</strong> {task.estimatedHours}</li>
                     )}
                     {task.type === 'JOB' && task.employmentType && (
                         <li><strong>Type:</strong> {task.employmentType.replace('_','-')}</li>
                     )}
                     {task.type === 'JOB' && task.duration && (
                         <li><strong>Duration:</strong> {task.duration}</li>
                     )}
                     {task.type === 'JOB' && task.compensation && (
                         <li><strong>Compensation:</strong> {task.compensation}</li>
                     )}
                 </ul>
             </div>

             {/* Action Buttons - Updated with conditional logic */} 
             <div className="action-buttons mb-3">
                 <h6 className="fw-bold">Actions</h6>
                 {user && isOwner && (
                    <> 
                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => onEdit(task)}>
                            <FontAwesomeIcon icon={faEdit} className="me-1" /> Edit
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => onDelete(task.id)}>
                            <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete
                        </Button>
                    </>
                 )}
                 {user && !isOwner && (
                    <>
                        {task.status === 'OPEN' && (
                             <Button variant="primary" size="sm" className="me-2" onClick={() => onApply(task.id)} >
                                <FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Apply
                             </Button>
                         )}
                        {task.owner && (
                            <> 
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    className="me-2" 
                                    onClick={() => onMessageOwner(task.ownerId, task.owner?.name ?? null)}
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} className="me-1" /> Message
                                </Button>
                                <Button 
                                    variant="outline-success" 
                                    size="sm" 
                                    onClick={() => onConnectOwner(task.ownerId, task.owner?.name ?? null)}
                                >
                                    <FontAwesomeIcon icon={faUserPlus} className="me-1" /> Connect
                                </Button>
                            </>
                        )}
                        {task.status !== 'OPEN' && <span className="text-muted small">This task is {task.status.toLowerCase()}.</span>}
                    </>
                 )}
                 {!user && task.status === 'OPEN' && (
                     <span className="text-muted small">Log in to apply or contact owner.</span>
                 )}
             </div>

             {/* Comments Section */}
             <div className="comments-section">
                 <TaskComments taskId={task.id} />
             </div>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default TaskCard; 